import './styles.css'

import { Checkbox, Dialog, DialogTrigger, InputFieldContainer } from '@/components'
import { ROUTE_FORGOT_PASSWORD, ROUTE_SIGN_UP } from '@/routes/paths'
import { lazy, useEffect, useState } from 'react'
import { resendSignUpCode, signIn } from 'aws-amplify/auth/cognito'

import { Button } from '@/components/ui/button'
import { Icons } from '@/theme/Images'
import { Input } from '@/components/ui/input'
import { convertToReadableString } from '@/lib/utils'
import { removeUserDetails } from '@/storage'
import {
  shouldRememberEmail,
  getInitialEmail,
  setEmailCache,
  getRememberedEmail,
  clearEmailCache,
  maskEmail,
  getEmailToSubmit,
} from '@/storage/email-cache'
import { signInSchema } from '@/schema/sign-in'
import { useAuth } from '../../contexts/auth.context'
import { useFormik } from 'formik'
import { useNavigate } from 'react-router-dom'
import LoadingScreen from '@/components/loading-screen'
import EyeOpen from '@/assets/icons/eye-open'
import EyeClosed from '@/assets/icons/eye-closed'
import { getCurrentYear } from '@/utils/date-format-util'
import { setSessionExpired, isSessionExpired } from '@/storage'
import { toast } from 'sonner'
import { ToastMessage } from '@/components/toast-message'

const MFADialog = lazy(() => import('./mfa-dialog'))

function SignInPage() {
  const navigate = useNavigate()
  const { loading, setLoading, oAuthError } = useAuth()
  const [codeDestination, setCodeDestination] = useState(null)
  const [loginNext, setLoginNext] = useState(null)
  const [loginError, setLoginError] = useState()
  const [isDialogOpen, setIsDialogOpen] = useState()

  const [showPassword, setShowPassword] = useState(false)
  const [rememberEmail, setRememberEmail] = useState(shouldRememberEmail())

  useEffect(() => {
    const isSExpired = isSessionExpired()
    // Route change preventing toast to persist that's why we are using local storage to persist this
    if (isSExpired) {
      setTimeout(() => {
        toast.custom(() => (
          <ToastMessage
            dismissible={false}
            success={false}
            text="Session Expired"
            description="Your session has expired due to inactivity. Please log in again to continue."
          />
        ))
      }, 500)
      setSessionExpired(false)
    }
    if (oAuthError) {
      setLoginError(oAuthError)
    }
  }, [oAuthError])

  const formik = useFormik({
    initialValues: {
      email: getInitialEmail(),
      password: '',
    },
    validate: values => {
      const errors = {}

      // Email validation
      if (!values.email) {
        errors.email = 'Email is required'
      } else {
        // Check if masked email matches cached email (skip Yup validation)
        const storedEmail = getRememberedEmail()
        if (!storedEmail || values.email !== maskEmail(storedEmail)) {
          // Use Yup validation for regular emails
          try {
            signInSchema.validateSyncAt('email', values)
          } catch (yupError) {
            errors.email = yupError.message
          }
        }
      }
      // Password validation
      if (!values.password) {
        errors.password = 'Password is required'
      }
      // Clear external login error if fields are empty
      if (!values.email && !values.password) {
        setLoginError(null)
      }
      return errors
    },
    onSubmit: async values => {
      setLoading(true)
      removeUserDetails()

      // Get email to submit (handles masked email case)
      const emailToSubmit = getEmailToSubmit(values.email)

      try {
        const { isSignedIn, nextStep } = await signIn({ username: emailToSubmit, password: values.password })
        if (!isSignedIn) {
          switch (nextStep.signInStep) {
            case 'CONFIRM_SIGN_UP':
              resendSignUpCode({
                username: emailToSubmit,
                options: {
                  clientMetadata: {
                    domain: import.meta.env.VITE_APP_DOMAIN,
                  },
                },
              })
              break
            case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
              // Save email when MFA is required (credentials validated successfully)
              handleEmailCaching(emailToSubmit, rememberEmail)
              setCodeDestination(nextStep.codeDeliveryDetails.destination)
              setLoginNext(nextStep.signInStep)
              setIsDialogOpen(true)
              break

            default:
              break
          }
          setLoginError(null)
          setLoading(false)
        } else {
          // Login successful - handle email caching based on remember preference
          handleEmailCaching(emailToSubmit, rememberEmail)
        }
      } catch (error) {
        console.error(error)
        setCodeDestination(null)
        setLoginNext(null)
        setLoginError(
          error.message === 'Incorrect username or password.'
            ? 'Incorrect password. Please try again or reset it.'
            : error.message?.includes('Too many requests')
              ? 'Please wait 2 minutes before requesting another verification code.'
              : error.message
        )
        setLoading(false)
      }
    },
  })

  // Helper function to handle email caching based on remember preference
  const handleEmailCaching = (email, shouldRemember) => {
    if (shouldRemember) {
      setEmailCache(email)
    } else {
      clearEmailCache()
    }
  }

  // Clear error message when any input field is focused
  const handleInputFocus = () => {
    setLoginError(null)
  }

  // Handle email change - clear stored email if input changes and there's a stored email
  const handleEmailChange = e => {
    const storedEmail = getRememberedEmail()

    // If there's a stored email and user is typing, clear it and uncheck remember
    if (storedEmail) {
      clearEmailCache()
      setRememberEmail(false)
    }

    formik.handleChange(e)
  }

  if (loading) return <LoadingScreen />
  return (
    <div className="font-poppins login-screen flex flex-row items-center justify-end min-w-fit h-dvh md:bg-[--neutral-25]">
      <div className="flex flex-col items-center md:justify-center self-stretch w-full px-5">
        <div className="flex flex-col items-center self-stretch py-0">
          <div className="absolute flex top-8 md:left-8 bg-contain bg-no-repeat">
            <img src={Icons.fastenTextLogoBlack} alt="fasten text logo" className="w-[130px] h-[34px]" />
          </div>
          <div className="flex flex-col md:p-8 md:bg-white  md:w-[520px] rounded-2xl max-md:w-full max-md:max-w-[360px] max-md:mt-8">
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="flex flex-col items-center self-center max-md:py-[70px]">
                <div className="flex flex-col items-start self-stretch gap-3 mb-8">
                  <p className="text-2xl md:text-[32px] font-semibold text-[--neutral-900]">Log In</p>
                </div>
                <div className="flex flex-col items-center self-stretch mb-4">
                  <div className="flex flex-col self-stretch gap-5">
                    <InputFieldContainer label="Email" labelClassName="text-[#1A2022]">
                      <Input
                        type="email"
                        id="email"
                        placeholder="Enter your email"
                        autoComplete="username"
                        onFocus={handleInputFocus}
                        onChange={handleEmailChange}
                        onBlur={formik.handleBlur}
                        className={formik.errors.email ? ' border-[--error-500]' : ''}
                        value={formik.values.email}
                        name="email"
                        aria-describedby={formik.errors.email ? 'email-error' : undefined} // for accssibility
                      />
                      {formik.errors.email && (
                        <p className="text-sm font-normal text-[--error-500]" role="alert">
                          {formik.errors.email}
                        </p>
                      )}
                    </InputFieldContainer>
                    <InputFieldContainer label="Password" className="relative" labelClassName="text-[#1A2022]">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        onFocus={handleInputFocus}
                        className={
                          (formik.errors.password && formik.touched.password) ||
                          loginError === 'Incorrect password. Please try again or reset it.'
                            ? ' border-[--error-500]'
                            : ''
                        }
                        {...formik.getFieldProps('password')}
                        data-testid="password"
                        aria-describedby={formik.errors.password ? 'password-error' : undefined} // for accssibility
                      />
                      <button
                        id="login-password-visibility-toggle-button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        type="button"
                        disabled={!formik.values.password}
                        className={`absolute right-3 ${(formik.errors.password && formik.touched.password) || loginError ? 'top-[36%]' : 'top-[50%]'} h-6 w-5 pl-1 bg-white`}
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="show-password-button"
                      >
                        {showPassword ? (
                          <EyeOpen stroke={`${formik.values.password ? '#98a2b3' : '#adbfc8'}`} />
                        ) : (
                          <EyeClosed stroke={`${formik.values.password ? '#98a2b3' : '#adbfc8'}`} />
                        )}
                      </button>
                      {formik.touched.password && formik.errors.password && (
                        <p className="text-sm font-normal text-[--error-500] " role="alert">
                          {formik.errors.password}
                        </p>
                      )}
                      {loginError ? (
                        <p className="text-sm font-normal text-[--error-500]" role="alert">
                          {loginError}
                        </p>
                      ) : null}
                    </InputFieldContainer>
                  </div>
                  <div className="flex items-center self-stretch w-full justify-between mt-2">
                    <div className="flex">
                      <Checkbox
                        id="remember-checkbox"
                        role="checkbox"
                        aria-label="Remember me"
                        checked={rememberEmail}
                        onCheckedChange={setRememberEmail}
                      />
                      <label
                        htmlFor="remember-checkbox"
                        className="text-sm sm:mr-10 font-normal leading-none text-[#1A2022] cursor-pointer select-none"
                      >
                        Remember me
                      </label>
                    </div>
                    <Button
                      id="forgot-password-button"
                      type="button"
                      role="button"
                      aria-label="Forgot password"
                      variant="link"
                      size="text"
                      tabIndex="0"
                      data-testid="forgot-password"
                      onClick={() => navigate(ROUTE_FORGOT_PASSWORD)}
                    >
                      Forgot Password
                    </Button>
                  </div>
                  {loginNext ? (
                    <p className="text-sm font-semibold text-[--brand-500]">{convertToReadableString(loginNext)}</p>
                  ) : null}
                  <div className="flex flex-col items-start self-stretch gap-4 mt-10">
                    {isDialogOpen ? (
                      <Dialog open={isDialogOpen} onOpenChange={open => open && setIsDialogOpen(true)} modal={true}>
                        <MFADialog
                          codeDestination={codeDestination}
                          email={formik.values.email}
                          password={formik.values.password}
                          onClose={() => {
                            setIsDialogOpen(false), setLoginNext(null)
                          }}
                        />
                      </Dialog>
                    ) : null}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          id="signin-button"
                          type="submit"
                          disabled={!formik.isValid || loading}
                          aria-label="Sign In"
                          role="button"
                          className="font-medium"
                        >
                          Sign In
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  </div>
                </div>
                {import.meta.env.VITE_DISABLE_SIGN_UP !== 'true' && (
                  <div className="flex content-center gap-1">
                    <p className="text-sm text-[#1A2022]">Don&apos;t have an account?</p>
                    <Button
                      type="button"
                      id="signup-button"
                      aria-label="Sign up"
                      role="button"
                      variant="link"
                      size="text"
                      onClick={() => {
                        removeUserDetails()
                        navigate(ROUTE_SIGN_UP)
                      }}
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <p className="absolute bottom-8 left-8 text-sm text-[--gray-800] max-md:hidden">
        © {getCurrentYear()} Fasten Rewards Inc.
      </p>
    </div>
  )
}

export default SignInPage
